/**
 * Emptying part of a brewery while the product is being built, as a browser clears its data one kind
 * at a time (Tyler, 2026-10-07: Scott rebuilt the brewery's equipment after every reset). The parts and
 * the ties between them are here so the page and the server agree: a part another depends on cannot
 * stay behind when that one goes, so choosing it chooses those too, and the page says why.
 *
 * The ties follow what refers to what in the database. Brews take their stock from inventory's lots
 * and the movement log names the brew that took it, so brew history and inventory go together; every
 * brew is of a recipe, so recipes take brews and the calendar; invoices are where stock came from and
 * name its items and lots; and stock sits in locations.
 */
export const RESET_PARTS = [
  "inventory",
  "invoices",
  "brews",
  "recipes",
  "calendar",
  "equipment",
  "locations",
  "settings",
  "notifications",
] as const;
export type ResetPart = (typeof RESET_PARTS)[number];

interface PartInfo {
  label: string;
  /** What it holds, as the page lists it. */
  holds: string;
  /** The parts that go with it, and why. */
  takes: readonly { part: ResetPart; why: string }[];
  /** What happens to the rest when it goes alone. */
  leaves?: string;
}

export const RESET_PART_INFO: Record<ResetPart, PartInfo> = {
  inventory: {
    label: "Inventory",
    holds: "Items, their stock, counts and harvests, and suppliers",
    takes: [
      { part: "brews", why: "brews took their stock from it" },
      { part: "invoices", why: "they are where its stock came from" },
    ],
    leaves: "Recipes stay, with their ingredients no longer linked to inventory.",
  },
  invoices: {
    label: "Invoices and imports",
    holds: "Invoices, their documents, and imports from Ollie",
    takes: [],
    leaves: "Stock stays, with no invoice to open from its lots.",
  },
  brews: {
    label: "Brew history",
    holds: "Brews started or brewed, with their pulls, readings and harvests",
    takes: [{ part: "inventory", why: "the log keeps which brew took its stock" }],
  },
  recipes: {
    label: "Recipes",
    holds: "Recipes, their versions, history, drafts and favorites",
    takes: [
      { part: "brews", why: "every brew is of a recipe" },
      { part: "calendar", why: "every planned brew is of a recipe" },
    ],
  },
  calendar: {
    label: "Calendar",
    holds: "Planned brews and recurring brews",
    takes: [],
  },
  equipment: {
    label: "Equipment",
    holds: "Brew systems, tanks and the brewery layout",
    takes: [],
    leaves: "Brews keep everything but their tank bookings, and recipes keep their figures.",
  },
  locations: {
    label: "Locations",
    holds: "Locations and where deliveries go; the starting ones are put back",
    takes: [{ part: "inventory", why: "its stock is in them" }],
  },
  settings: {
    label: "Packaging and maker names",
    holds: "Your own pack sizes and the names shown in front of each maker",
    takes: [],
    leaves: "The standard maker names are put back.",
  },
  notifications: {
    label: "Notifications",
    holds: "Everything in the notification bell",
    takes: [],
  },
};

/** Everything that goes when these do: each part with the parts it takes, and theirs, in order. */
export function resetClosure(parts: readonly ResetPart[]): ResetPart[] {
  const chosen = new Set<ResetPart>(parts);
  let grew = true;
  while (grew) {
    grew = false;
    for (const part of [...chosen]) {
      for (const { part: taken } of RESET_PART_INFO[part].takes) {
        if (!chosen.has(taken)) {
          chosen.add(taken);
          grew = true;
        }
      }
    }
  }
  return RESET_PARTS.filter((p) => chosen.has(p));
}

/**
 * Why each part that was not chosen goes anyway: the chosen part (or one it took) that takes it, and
 * the reason, for the page to say beside it.
 */
export function resetReasons(parts: readonly ResetPart[]): Map<ResetPart, string> {
  const chosen = new Set(parts);
  const reasons = new Map<ResetPart, string>();
  const queue = [...parts];
  while (queue.length) {
    const part = queue.shift() as ResetPart;
    for (const { part: taken, why } of RESET_PART_INFO[part].takes) {
      if (chosen.has(taken) || reasons.has(taken)) continue;
      reasons.set(taken, `Goes with ${RESET_PART_INFO[part].label.toLowerCase()}: ${why}.`);
      queue.push(taken);
    }
  }
  return reasons;
}
